import { useEffect, useState, useCallback, useMemo } from "react";
import { api, LIABILITY_TYPE_LABELS, liabilityDue, liabilityCriticalDate } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Scales, PencilSimple, Trash, WarningCircle, House, Link as LinkIcon, CalendarBlank, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

import { centsToUSD } from "@/lib/morning";

/* ── Helpers ── */
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toCents = (v) => { const n = numOrNull(v); return n != null ? Math.round(n * 100) : null; };
const fromCents = (v) => (v != null && v !== "" ? Number(v) / 100 : "");
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const daysLabel = (days) => {
  if (days < 0) return `${Math.abs(days)}d ago`;
  if (days === 0) return "today";
  if (days <= 30) return `in ${days}d`;
  const mo = Math.round(days / 30);
  return mo <= 24 ? `in ${mo} mo` : `in ${Math.round(days / 365)} yr`;
};

const EMPTY_FORM = {
  name: "", liability_type: "mortgage_traditional", lender: "", asset_id: null, entity_id: null,
  owner_party: "joint", original_balance: "", current_balance: "", interest_rate: "",
  monthly_payment: "", due_date: "", notes: "", loan_number: "",
  origination_date: "", maturity_date: "", io_period_end_date: "", post_io_plan: "undecided",
  draw_period_end_date: "", credit_limit: "",
};

const TYPE_GROUPS = [
  { key: "mortgage_traditional", label: "Traditional Mortgages" },
  { key: "mortgage_dscr_io", label: "DSCR Interest-Only" },
  { key: "heloc", label: "HELOCs" },
  { key: "loan", label: "Loans" },
  { key: "line_of_credit", label: "Lines of Credit" },
  { key: "other", label: "Other" },
];

const POST_IO_OPTIONS = [
  { value: "refinance", label: "Refinance" },
  { value: "convert_to_amortizing", label: "Convert to amortizing" },
  { value: "sell", label: "Sell property" },
  { value: "undecided", label: "Undecided" },
];

export default function Liabilities() {
  const { fmtAsset } = useDemo();
  const { hid, isOperator, ownerLabels, ownerLabel, entities, assets, refreshLiabilities } = useHousehold();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const realEstate = (assets || []).filter((a) => a.asset_type === "real_estate");

  const load = useCallback(() => {
    if (!hid) return;
    api.get(`/liabilities?household_id=${hid}`).then((r) => setItems(r.data)).catch(() => {});
  }, [hid]);
  useEffect(() => { load(); }, [load]);

  const assetName = (id) => (assets || []).find((a) => a.id === id)?.name || "—";
  const entName = (id) => (entities || []).find((e) => e.id === id)?.name || null;

  const totalOwed = items.reduce((s, l) => s + Number(l.current_balance || 0), 0);
  const fmtCents = (v) => centsToUSD(v);

  // Critical-date watch list: liabilities with a lifecycle date within 24 months
  const watchList = useMemo(() => {
    const cutoff = 24 * 30; // ~24 months in days
    return items
      .map((l) => ({ ...l, _crit: liabilityCriticalDate(l) }))
      .filter((l) => l._crit.has && l._crit.days >= 0 && l._crit.days <= cutoff)
      .sort((a, b) => a._crit.days - b._crit.days);
  }, [items]);
  const watchIds = new Set(watchList.map((l) => l.id));

  // Group items by type for the card grid
  const grouped = useMemo(() => {
    return TYPE_GROUPS
      .map((g) => ({ ...g, items: items.filter((l) => l.liability_type === g.key) }))
      .filter((g) => g.items.length > 0);
  }, [items]);

  const openCreate = () => { setForm(EMPTY_FORM); setEditId(null); setOpen(true); };
  const openEdit = (l) => {
    setForm({
      ...EMPTY_FORM, ...l,
      original_balance: fromCents(l.original_balance), current_balance: fromCents(l.current_balance),
      interest_rate: l.interest_rate ?? "", monthly_payment: fromCents(l.monthly_payment),
      due_date: l.due_date || "", asset_id: l.asset_id || null, entity_id: l.entity_id || null,
      origination_date: l.origination_date || "", maturity_date: l.maturity_date || "",
      io_period_end_date: l.io_period_end_date || "", post_io_plan: l.post_io_plan || "undecided",
      draw_period_end_date: l.draw_period_end_date || "", credit_limit: fromCents(l.credit_limit),
      loan_number: l.loan_number || "",
    });
    setEditId(l.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), liability_type: form.liability_type, lender: form.lender,
      loan_number: form.loan_number || "", owner_party: form.owner_party, notes: form.notes,
      decided_by: form.owner_party,
      asset_id: form.asset_id || null, entity_id: form.entity_id || null,
      original_balance: toCents(form.original_balance) ?? 0,
      current_balance: toCents(form.current_balance) ?? 0,
      interest_rate: numOrNull(form.interest_rate),
      monthly_payment: toCents(form.monthly_payment),
      due_date: form.due_date || null,
      origination_date: form.origination_date || null,
      maturity_date: form.maturity_date || null,
      io_period_end_date: form.io_period_end_date || null,
      post_io_plan: form.post_io_plan || null,
      draw_period_end_date: form.draw_period_end_date || null,
      credit_limit: toCents(form.credit_limit),
    };
    try {
      if (editId) await api.put(`/liabilities/${editId}?household_id=${hid}`, payload);
      else await api.post(`/liabilities?household_id=${hid}`, payload);
      toast.success(editId ? "Liability updated" : "Liability added");
      setOpen(false); load(); refreshLiabilities();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this liability?")) return;
    try {
      await api.delete(`/liabilities/${id}?household_id=${hid}`);
      toast.success("Deleted"); load(); refreshLiabilities();
    } catch (e) { toast.error("Delete failed"); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-8 animate-fade-up min-h-screen -m-6 p-6 lg:-m-10 lg:p-10 bg-[hsl(var(--ploy-bg))]" data-testid="liabilities-page">
      {/* ── Page hero ── */}
      <div className="flex items-start justify-between gap-4 pb-6 border-b border-[hsl(var(--ploy-border))]">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] flex items-center justify-center shadow-sm shrink-0">
            <Scales size={22} weight="regular" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
              Operations · Debts &amp; Financing
            </p>
            <h1 className="font-serif font-medium tracking-tight text-[44px] sm:text-[52px] leading-[1.05] mt-1 text-[hsl(var(--ploy-ink))]" data-testid="page-title">
              Liabilities
            </h1>
            <p className="text-sm text-[hsl(var(--ploy-ink-soft))] mt-2 max-w-xl leading-relaxed">
              Track outstanding mortgages, DSCR loans, HELOCs, and other financing — and link each to the property it secures.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-5 shrink-0 pt-2">
          {items.length > 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))]">Total Owed</p>
              <p data-testid="liabilities-total" className="font-mono text-2xl font-bold tabular-nums text-[hsl(var(--ploy-rust))]">{fmtCents(totalOwed)}</p>
              <p className="text-[11px] text-[hsl(var(--ploy-ink-soft))] tabular-nums">{items.length} {items.length === 1 ? "liability" : "liabilities"}</p>
            </div>
          )}
          {isOperator && (
            <Button
              data-testid="add-liability-button"
              onClick={openCreate}
              className="rounded-[10px] font-semibold bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] hover:bg-[hsl(var(--ploy-ink))]/90 border-0 gap-1.5"
            >
              <Plus size={14} weight="bold" /> Add Liability
            </Button>
          )}
        </div>
      </div>

      {/* ── Critical-date watch list ── */}
      {watchList.length > 0 && (
        <div className="bg-[hsl(var(--ploy-panel))] border border-[hsl(var(--ploy-border))] rounded-2xl p-5" data-testid="critical-date-watchlist">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} weight="fill" className="text-[hsl(var(--ploy-brass-deep))]" />
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))]">Critical dates within 24 months</p>
          </div>
          <div className="space-y-2">
            {watchList.map((l) => (
              <div key={l.id} data-testid={`watch-item-${l.id}`} className="flex items-center justify-between gap-4 bg-[hsl(var(--ploy-card))] rounded-xl px-4 py-2.5 border border-[hsl(var(--ploy-border))]">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[hsl(var(--ploy-ink))] truncate">{l.name}</p>
                  <p className="text-[11px] text-[hsl(var(--ploy-ink-soft))]">{LIABILITY_TYPE_LABELS[l.liability_type] || l.liability_type} · {l._crit.label}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[13px] font-mono font-semibold tabular-nums text-[hsl(var(--ploy-ink))]">{fmtDate(l._crit.date)}</p>
                  <p className={`text-[11px] font-bold tabular-nums ${l._crit.days <= 180 ? "text-[hsl(var(--ploy-rust))]" : "text-[hsl(var(--ploy-brass-deep))]"}`}>
                    {daysLabel(l._crit.days)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Card grid ── */}
      {items.length === 0 ? (
        <div className="bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] rounded-2xl p-12 text-center" data-testid="liabilities-empty">
          <Scales size={32} className="mx-auto text-[hsl(var(--ploy-ink-soft))] mb-3 opacity-60" />
          <p className="font-serif text-xl text-[hsl(var(--ploy-ink))]">No liabilities yet</p>
          <p className="text-sm text-[hsl(var(--ploy-ink-soft))] mt-1">Add your first one to start tracking outstanding debts and financing.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <div key={g.key}>
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))] mb-3">{g.label}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map((l) => (
                  <LiabilityCard
                    key={l.id} l={l} fmtCents={fmtCents}
                    assetName={assetName} entName={entName} ownerLabel={ownerLabel}
                    isOperator={isOperator} onEdit={openEdit} onDelete={remove}
                    hasWatch={watchIds.has(l.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create/Edit Dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-[hsl(var(--ploy-card))] border-[hsl(var(--ploy-border))] max-h-[88vh] overflow-y-auto" data-testid="liability-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight text-[hsl(var(--ploy-ink))]">{editId ? "Edit Liability" : "New Liability"}</DialogTitle>
            <DialogDescription className="text-[hsl(var(--ploy-ink-soft))] text-sm">
              {editId ? "Update the details below." : "Choose a type, then fill in the details."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {/* Type picker — renders first so form morphs */}
            <Field label="Type" testid="liability-type-select">
              <Select value={form.liability_type} onValueChange={(v) => set("liability_type", v)}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LIABILITY_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" testid="liability-name-input">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. 123 Main St Mortgage" className="rounded-sm h-9" />
              </Field>
              <Field label="Lender / Servicer" testid="liability-lender-input">
                <Input value={form.lender} onChange={(e) => set("lender", e.target.value)} className="rounded-sm h-9" />
              </Field>
            </div>

            {/* Monetary fields — all types */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Original Balance" testid="liability-original-input">
                <Input type="number" value={form.original_balance} onChange={(e) => set("original_balance", e.target.value)} className="rounded-sm h-9 font-mono" />
              </Field>
              <Field label="Current Balance" testid="liability-current-input">
                <Input type="number" value={form.current_balance} onChange={(e) => set("current_balance", e.target.value)} className="rounded-sm h-9 font-mono" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Interest %" testid="liability-rate-input">
                <Input type="number" step="0.001" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} className="rounded-sm h-9 font-mono" />
              </Field>
              <Field label="Monthly Payment" testid="liability-payment-input">
                <Input type="number" value={form.monthly_payment} onChange={(e) => set("monthly_payment", e.target.value)} className="rounded-sm h-9 font-mono" />
              </Field>
              <Field label="Next Due Date" testid="liability-due-input">
                <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="rounded-sm h-9" />
              </Field>
            </div>

            {/* ── Type-specific fields ── */}
            {form.liability_type === "mortgage_traditional" && (
              <div className="grid grid-cols-2 gap-3 pt-1 border-t border-dashed border-[hsl(var(--ploy-border))]">
                <Field label="Origination Date">
                  <Input type="date" value={form.origination_date} onChange={(e) => set("origination_date", e.target.value)} className="rounded-sm h-9" />
                </Field>
                <Field label="Maturity Date">
                  <Input type="date" value={form.maturity_date} onChange={(e) => set("maturity_date", e.target.value)} className="rounded-sm h-9" />
                </Field>
              </div>
            )}
            {form.liability_type === "mortgage_dscr_io" && (
              <div className="space-y-3 pt-1 border-t border-dashed border-[hsl(var(--ploy-border))]">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="IO Balloon Date *" testid="liability-io-end-input">
                    <Input type="date" value={form.io_period_end_date} onChange={(e) => set("io_period_end_date", e.target.value)} className="rounded-sm h-9" />
                  </Field>
                  <Field label="Post-IO Plan *" testid="liability-post-io-select">
                    <Select value={form.post_io_plan || "undecided"} onValueChange={(v) => set("post_io_plan", v)}>
                      <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {POST_IO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Origination Date">
                    <Input type="date" value={form.origination_date} onChange={(e) => set("origination_date", e.target.value)} className="rounded-sm h-9" />
                  </Field>
                  <Field label="Maturity Date">
                    <Input type="date" value={form.maturity_date} onChange={(e) => set("maturity_date", e.target.value)} className="rounded-sm h-9" />
                  </Field>
                </div>
              </div>
            )}
            {form.liability_type === "heloc" && (
              <div className="space-y-3 pt-1 border-t border-dashed border-[hsl(var(--ploy-border))]">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Credit Limit *" testid="liability-credit-limit-input">
                    <Input type="number" value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} className="rounded-sm h-9 font-mono" />
                  </Field>
                  <Field label="Draw Period End *" testid="liability-draw-end-input">
                    <Input type="date" value={form.draw_period_end_date} onChange={(e) => set("draw_period_end_date", e.target.value)} className="rounded-sm h-9" />
                  </Field>
                </div>
              </div>
            )}

            {/* Secured by / Entity / Owner — all types */}
            <div className="pt-1 border-t border-dashed border-[hsl(var(--ploy-border))] space-y-3">
              <Field label="Secured by (property)" testid="liability-asset-select">
                <Select value={form.asset_id || "__none__"} onValueChange={(v) => set("asset_id", v === "__none__" ? null : v)}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Unsecured / none" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Unsecured —</SelectItem>
                    {realEstate.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Entity (optional)" testid="liability-entity-select">
                  <Select value={form.entity_id || "__none__"} onValueChange={(v) => set("entity_id", v === "__none__" ? null : v)}>
                    <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {(entities || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Owner" testid="liability-owner-select">
                  <Select value={form.owner_party} onValueChange={(v) => set("owner_party", v)}>
                    <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ownerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Notes">
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm" rows={2} />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-liability-button"
              onClick={save}
              disabled={saving}
              className="w-full rounded-[10px] font-semibold bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] hover:bg-[hsl(var(--ploy-ink))]/90 border-0"
            >
              {saving ? "Saving…" : editId ? "Save Changes" : "Add Liability"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Sub-components ── */
const Field = ({ label, testid, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-[hsl(var(--ploy-brass-deep))]">{label}</Label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);

function LiabilityCard({ l, fmtCents, assetName, entName, ownerLabel, isOperator, onEdit, onDelete, hasWatch }) {
  const due = liabilityDue(l.due_date);
  const crit = liabilityCriticalDate(l);
  const paidPct = l.original_balance > 0 ? Math.max(0, Math.min(100, Math.round((1 - Number(l.current_balance || 0) / Number(l.original_balance)) * 100))) : 0;

  return (
    <div
      data-testid={`liability-card-${l.id}`}
      className={`bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] rounded-2xl p-5 group
        shadow-[0_4px_20px_hsl(var(--ploy-ink)/0.04)]
        ${hasWatch ? "border-t-[3px] border-t-[hsl(var(--accent))]" : ""}`}
    >
      <div className="flex items-start justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))]">
          {LIABILITY_TYPE_LABELS[l.liability_type] || l.liability_type}
        </span>
        {isOperator && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button data-testid={`edit-liability-${l.id}`} onClick={() => onEdit(l)} className="text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))]"><PencilSimple size={15} /></button>
            <button data-testid={`delete-liability-${l.id}`} onClick={() => onDelete(l.id)} className="text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-rust))]"><Trash size={15} /></button>
          </div>
        )}
      </div>
      <h3 className="font-serif text-lg font-medium tracking-tight mt-3 text-[hsl(var(--ploy-ink))]">{l.name}</h3>
      {l.lender && <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))]">{l.lender}</p>}
      <p className="font-mono text-2xl font-bold tabular-nums mt-1.5 text-[hsl(var(--ploy-ink))]">{fmtCents(l.current_balance)}</p>
      <p className="text-[11px] text-[hsl(var(--ploy-ink-soft))] mt-0.5 tabular-nums">
        {l.liability_type === "heloc" && l.credit_limit
          ? `of ${fmtCents(l.credit_limit)} limit · ${paidPct > 0 ? `${100 - paidPct}% used` : "no draw"}`
          : `of ${fmtCents(l.original_balance)} original${paidPct > 0 ? ` · ${paidPct}% paid down` : ""}`}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
        {l.interest_rate != null && <KV k="Rate" v={`${l.interest_rate}%`} />}
        {l.monthly_payment != null && <KV k="Payment" v={fmtCents(l.monthly_payment)} />}
        {l.due_date && <KV k="Next due" v={fmtDate(l.due_date)} warn={due.due} />}
        {l.maturity_date && <KV k="Maturity" v={fmtDate(l.maturity_date)} />}
        {l.io_period_end_date && <KV k="IO balloon" v={fmtDate(l.io_period_end_date)} />}
        {l.draw_period_end_date && <KV k="Draw ends" v={fmtDate(l.draw_period_end_date)} />}
        {l.post_io_plan && l.liability_type === "mortgage_dscr_io" && <KV k="Post-IO" v={POST_IO_OPTIONS.find((o) => o.value === l.post_io_plan)?.label || l.post_io_plan} />}
      </div>

      {due.due && (
        <p className="text-[12px] mt-2 inline-flex items-center gap-1.5 text-[hsl(var(--ploy-rust))] font-semibold">
          <WarningCircle size={13} weight="fill" /> {due.days < 0 ? "Payment overdue" : `Due in ${due.days}d`}
        </p>
      )}

      {(l.asset_id || entName(l.entity_id)) && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--ploy-border))] text-[12px] text-[hsl(var(--ploy-ink-soft))] space-y-1">
          {l.asset_id && <p className="flex items-center gap-1.5"><House size={13} weight="bold" /> Secured by {assetName(l.asset_id)}</p>}
          {entName(l.entity_id) && <p className="flex items-center gap-1.5"><LinkIcon size={13} weight="bold" /> {entName(l.entity_id)} · {ownerLabel(l.owner_party)}</p>}
        </div>
      )}
    </div>
  );
}

const KV = ({ k, v, warn }) => (
  <div>
    <span className="text-[hsl(var(--ploy-ink-soft))]">{k}</span>
    <p className={`font-mono font-semibold tabular-nums ${warn ? "text-[hsl(var(--ploy-rust))]" : "text-[hsl(var(--ploy-ink))]"}`}>{v}</p>
  </div>
);
